import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js"

export default class extends Controller {
  static values = {
    actions: Array, // Array of action objects: {label, action, icon, url, method, confirm}
    companyId: String
  }

  static targets = ["menu", "counter"]

  connect() {
    this.hide()
    this.renderActions()
  }

  // Listen for bulk selector events
  handleSelectionChanged(event) {
    const { hasSelection, count } = event.detail
    
    if (hasSelection) {
      this.updateCounter(count)
      this.show()
    } else {
      this.hide()
    }
  }

  show() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.remove('floating-menu--hidden')
      this.menuTarget.classList.add('floating-menu--visible')
    }
  }

  hide() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.remove('floating-menu--visible')
      this.menuTarget.classList.add('floating-menu--hidden')
    }
  }

  updateCounter(count) {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${count} selected`
    }
  }

  renderActions() {
    if (!this.hasMenuTarget || !this.actionsValue.length) return

    const actionsContainer = this.menuTarget.querySelector('.floating-menu__actions')
    if (!actionsContainer) return

    actionsContainer.innerHTML = ''

    this.actionsValue.forEach(action => {
      const button = document.createElement('button')
      button.className = `floating-menu__action ${action.danger ? 'floating-menu__action--danger' : ''}`
      button.dataset.action = `click->floating-action-menu#${action.action}`
      button.dataset.actionConfig = JSON.stringify(action)
      
      if (action.icon) {
        button.innerHTML = `<i class="${action.icon}"></i>`
      } else {
        button.textContent = action.label
      }
      
      if (action.label) {
        button.setAttribute('data-tippy-content', action.label)
        button.setAttribute('data-tippy-offset', '[0, 14]')
      }

      actionsContainer.appendChild(button)
    })
  }

  move(event) {
    const button = event.target.closest('button')
    const actionConfig = button?.dataset.actionConfig ? JSON.parse(button.dataset.actionConfig) : {}

    const bulkSelectorController = this.application.getControllerForElementAndIdentifier(this.element, 'bulk-selector')
    if (!bulkSelectorController || !bulkSelectorController.selectedIdsValue.length) return

    // Build the modal URL using stored values
    const url = new URL('/moves/new', window.location.origin)
    const entity = bulkSelectorController.entityValue
    const capitalizedEntity = entity.charAt(0).toUpperCase() + entity.slice(1)
    url.searchParams.set('move_type', actionConfig.move_type || `${capitalizedEntity}Folder`)
    url.searchParams.set('record_type', actionConfig.record_type || capitalizedEntity)
    url.searchParams.set('ids', JSON.stringify(bulkSelectorController.selectedIdsValue))
    url.searchParams.set('single_move', 'true')
    
    if (this.companyIdValue) {
      url.searchParams.set('company_id', this.companyIdValue)
    }

    const frame = actionConfig.turbo_frame || actionConfig.frame || 'modal'
    Turbo.visit(url.toString(), { frame })
  }

  change_labels(event) {
    const button = event.target.closest('button')
    const actionConfig = button?.dataset.actionConfig ? JSON.parse(button.dataset.actionConfig) : {}

    const bulkSelectorController = this.application.getControllerForElementAndIdentifier(this.element, 'bulk-selector')
    if (!bulkSelectorController || !bulkSelectorController.selectedIdsValue.length) return

    const url = new URL('/bulk_operations/change_labels/new', window.location.origin)
    const entity = bulkSelectorController.entityValue
    const capitalizedEntity = entity.charAt(0).toUpperCase() + entity.slice(1)
    url.searchParams.set('record_type', actionConfig.record_type || capitalizedEntity)
    url.searchParams.set('ids', JSON.stringify(bulkSelectorController.selectedIdsValue))

    if (this.companyIdValue) {
      url.searchParams.set('company_id', this.companyIdValue)
    }

    const frame = actionConfig.turbo_frame || actionConfig.frame || 'sidepanel'
    Turbo.visit(url.toString(), { frame })
  }

  delete(event) {
    const button = event.target.closest('button')
    const actionConfig = button?.dataset.actionConfig ? JSON.parse(button.dataset.actionConfig) : {}

    if (actionConfig.confirm_modal_url) {
      const bulkSelectorController = this.application.getControllerForElementAndIdentifier(this.element, 'bulk-selector')
      const selectedIds = bulkSelectorController ? bulkSelectorController.selectedIds : []
      if (!selectedIds.length) return

      const url = new URL(actionConfig.confirm_modal_url, window.location.origin)
      url.searchParams.set('ids', JSON.stringify(selectedIds))
      Turbo.visit(url.toString(), { frame: 'modal' })
      return
    }

    this.handleAction(event, 'delete')
  }

  archive(event) {
    this.handleAction(event, 'archive')
  }

  optional(event) {
    this.handleAction(event, 'optional')
  }

  required(event) {
    this.handleAction(event, 'required')
  }

  mark_as_subtask(event) {
    const button = event.target.closest('button')
    const actionConfig = button?.dataset.actionConfig ? JSON.parse(button.dataset.actionConfig) : {}

    const bulkSelectorController = this.application.getControllerForElementAndIdentifier(this.element, 'bulk-selector')
    const selectedIds = bulkSelectorController ? bulkSelectorController.selectedIds : []
    if (!selectedIds.length || !actionConfig.url) return

    const url = new URL(actionConfig.url, window.location.origin)
    url.searchParams.set('ids', JSON.stringify(selectedIds))
    this.close()
    Turbo.visit(url.toString(), { frame: 'modal' })
  }

  close() {
    // Clear the selection when closing
    // The bulk-selector controller is on the same element as floating-action-menu
    const bulkSelectorController = this.application.getControllerForElementAndIdentifier(this.element, 'bulk-selector')
    if (bulkSelectorController) {
      bulkSelectorController.clearSelection()
    }
    
    this.dispatch("close-requested")
    this.hide()
  }

  handleAction(event, actionType) {
    const button = event.target.closest('button')
    const actionConfig = button?.dataset.actionConfig ? JSON.parse(button.dataset.actionConfig) : {}
    
    // Get selected IDs from bulk selector (same element)
    const bulkSelectorController = this.application.getControllerForElementAndIdentifier(this.element, 'bulk-selector')
    const selectedIds = bulkSelectorController ? bulkSelectorController.selectedIds : []

    if (!selectedIds.length) return

    if (actionConfig.confirm_url) {
      const url = new URL(actionConfig.confirm_url, window.location.origin)
      url.searchParams.set('ids', JSON.stringify(selectedIds))
      Turbo.visit(url.toString(), { frame: 'modal' })
      return
    }

    if (actionConfig.confirm) {
      const confirmMessage = actionConfig.confirm.replace('{count}', selectedIds.length)
      if (!confirm(confirmMessage)) return
    }
    
    if (actionConfig.url) {
      this.makeApiCall(actionConfig, selectedIds)
    }
  }

  async makeApiCall(actionConfig, selectedIds) {
    try {
      const formData = new FormData()
      formData.append('ids', JSON.stringify(selectedIds))
      formData.append('action', actionConfig.action)
      const useTurboStream = actionConfig.response_mode === "turbo_stream"

      const response = await fetch(actionConfig.url, {
        method: actionConfig.method || 'POST',
        headers: {
          'Accept': useTurboStream ? 'text/vnd.turbo-stream.html' : 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      if (useTurboStream) {
        const streamMessage = await response.text()
        Turbo.renderStreamMessage(streamMessage)

        if (!response.ok) return

        this.close()
        return
      }

      let responseData = null
      try {
        responseData = await response.json()
      } catch (_) {
        responseData = null
      }

      if (!response.ok) {
        const errorMessage = responseData?.error || responseData?.message || `Request failed with status ${response.status}`
        throw new Error(errorMessage)
      }

      this.close()
      Turbo.visit(window.location.href, { action: "replace" })
    } catch (error) {
      flash(`Failed to perform bulk action: ${error.message}`, "error");
    }
  }
} 